import React, { useEffect, useMemo, useState } from "react";
import { getEventExtraMarkets } from "../actions/gameSettings";
import { toastAlert } from "../lib/toastAlert";
import {
  EXTRA_MARKET_GROUP_LABELS,
  EXTRA_MARKET_GROUP_ORDER,
  buildExtraMarketSlipItem,
  formatExtraMarketTitle,
  getOutcomeChipParts,
  isExtraMarketPlayable,
  orderExtraOutcomesForDisplay,
} from "../../utility/extraMarketPlay";

const INITIAL_VISIBLE = 12;

/**
 * + More markets: playable full-game alternates only (client: no preview /
 * ungradeable markets; no bookmaker labels).
 */
export default function EventExtraMarketsPanel({
  game,
  onToggleOutcome,
  isOutcomeSelected,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [openGroups, setOpenGroups] = useState(() => new Set(["alternates"]));
  const [expandedMarkets, setExpandedMarkets] = useState(() => new Set());

  useEffect(() => {
    const sportKey = game?.sport_key;
    const eventId = game?.id;
    if (!sportKey || !eventId) {
      setPayload(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setExpandedMarkets(new Set());

    getEventExtraMarkets({ sportKey, eventId })
      .then((resp) => {
        if (cancelled) return;
        if (resp?.status) {
          setPayload(resp.result || null);
          const groups = resp.result?.groups || {};
          const nextOpen = new Set();
          EXTRA_MARKET_GROUP_ORDER.forEach((key) => {
            if ((groups[key] || []).length) nextOpen.add(key);
          });
          if (nextOpen.has("alternates")) {
            setOpenGroups(new Set(["alternates"]));
          } else if (nextOpen.size) {
            setOpenGroups(new Set([[...nextOpen][0]]));
          }
        } else {
          setPayload(null);
          setError(resp?.message || "Extra markets unavailable");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPayload(null);
          setError("Could not load extra markets");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [game?.id, game?.sport_key]);

  const groupEntries = useMemo(() => {
    if (!payload?.groups) return [];
    return EXTRA_MARKET_GROUP_ORDER.map((key) => ({
      key,
      label: EXTRA_MARKET_GROUP_LABELS[key],
      markets: (payload.groups[key] || []).filter((m) =>
        isExtraMarketPlayable(m.key)
      ),
    })).filter((g) => g.markets.length > 0);
  }, [payload]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleMarketExpanded = (marketKey) => {
    setExpandedMarkets((prev) => {
      const next = new Set(prev);
      if (next.has(marketKey)) next.delete(marketKey);
      else next.add(marketKey);
      return next;
    });
  };

  const extraCheckId = (marketKey, outcome) =>
    `${game.id}-extra-${marketKey}-${String(outcome?.name || "")}-${String(
      outcome?.point ?? ""
    )}-${String(outcome?.price ?? "")}`;

  const handleOutcomeClick = (market, outcome) => {
    const marketKey = market.key;
    const checkId = extraCheckId(marketKey, outcome);

    if (!isExtraMarketPlayable(marketKey)) return;
    if (typeof onToggleOutcome !== "function") return;

    const selected =
      typeof isOutcomeSelected === "function"
        ? isOutcomeSelected(checkId, game.id)
        : false;

    const slipItem = buildExtraMarketSlipItem({
      game,
      marketKey,
      outcome,
      checkId,
    });
    if (!slipItem) {
      toastAlert(
        "error",
        "This selection cannot be added right now.",
        "extra-market-block"
      );
      return;
    }

    const fakeEvent = { target: { checked: !selected } };
    Promise.resolve(onToggleOutcome(fakeEvent, slipItem)).catch(() => {});
  };

  if (!game?.id) return null;

  return (
    <section className="event-extra-markets" aria-label="More markets">
      <div className="event-extra-markets__head">
        <h3 className="event-extra-markets__title">+ More markets</h3>
        <p className="event-extra-markets__hint">
          Alternate full-game spreads and totals you can add to your entry.
        </p>
      </div>

      {loading ? (
        <p className="event-extra-markets__status" role="status">
          Loading extra markets…
        </p>
      ) : null}

      {!loading && error ? (
        <p
          className="event-extra-markets__status event-extra-markets__status--error"
          role="status"
        >
          {error}
        </p>
      ) : null}

      {!loading && !error && groupEntries.length === 0 ? (
        <p className="event-extra-markets__status" role="status">
          No extra markets for this event right now.
        </p>
      ) : null}

      {!loading &&
        groupEntries.map(({ key, label, markets }) => {
          const isOpen = openGroups.has(key);
          return (
            <div key={key} className="event-extra-markets__group">
              <button
                type="button"
                className="event-extra-markets__group-toggle"
                aria-expanded={isOpen}
                onClick={() => toggleGroup(key)}
              >
                <span>{label}</span>
                <span className="event-extra-markets__group-meta">
                  {markets.length} market{markets.length === 1 ? "" : "s"}
                  <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
                </span>
              </button>

              {isOpen ? (
                <ul className="event-extra-markets__list">
                  {markets.map((market) => {
                    const outcomes = orderExtraOutcomesForDisplay(
                      market.outcomes || [],
                      market.key,
                      game
                    );
                    const expanded = expandedMarkets.has(market.key);
                    const visible = expanded
                      ? outcomes
                      : outcomes.slice(0, INITIAL_VISIBLE);
                    const hiddenCount = Math.max(
                      0,
                      outcomes.length - INITIAL_VISIBLE
                    );

                    return (
                      <li key={market.key} className="event-extra-markets__item">
                        <div className="event-extra-markets__market-head">
                          <span className="event-extra-markets__market-name">
                            {formatExtraMarketTitle(market.key)}
                          </span>
                        </div>

                        <div
                          className="event-extra-markets__outcomes"
                          role="group"
                        >
                          {visible.map((outcome, outcomeIndex) => {
                            const checkId = extraCheckId(market.key, outcome);
                            const selected =
                              typeof isOutcomeSelected === "function" &&
                              isOutcomeSelected(checkId, game.id);
                            const chip = getOutcomeChipParts(
                              outcome,
                              market.key
                            );

                            return (
                              <button
                                key={checkId || `${market.key}-${outcomeIndex}`}
                                type="button"
                                className={`event-extra-markets__chip${
                                  selected
                                    ? " event-extra-markets__chip--selected"
                                    : ""
                                }`}
                                aria-pressed={!!selected}
                                aria-label={chip.fullLabel}
                                title={chip.fullLabel}
                                onClick={() =>
                                  handleOutcomeClick(market, outcome)
                                }
                              >
                                {chip.title ? (
                                  <span className="event-extra-markets__chip-title">
                                    {chip.title}
                                  </span>
                                ) : null}
                                <span className="event-extra-markets__chip-meta">
                                  {chip.line ? (
                                    <span className="event-extra-markets__chip-line">
                                      {chip.line}
                                    </span>
                                  ) : null}
                                  {chip.price ? (
                                    <span className="event-extra-markets__chip-price">
                                      {chip.price}
                                    </span>
                                  ) : null}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {hiddenCount > 0 ? (
                          <button
                            type="button"
                            className="event-extra-markets__show-more"
                            onClick={() => toggleMarketExpanded(market.key)}
                          >
                            {expanded
                              ? "Show less"
                              : `+${hiddenCount} more`}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
          );
        })}
    </section>
  );
}
